import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwSearchComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  DialogFormControls,
  DialogOptions,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import {
  CreateCommunicationPropertiesRequest,
  UpdateCommunicationPropertiesRequest,
} from '../../../../api/models';
import { OperationTypes } from '../../../../shared/constants/operation-types.constants';
import {
  catchError,
  concatMap,
  finalize,
  from,
  map,
  of,
  switchMap,
  toArray,
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { ThemeService } from '../../../../core/services/theme.service';
import { CommunicationPropertiesService } from '../../../../core/services/communication-properties.service';
import { UserService } from '../../../../core/services/user.service';

@Component({
  selector: 'app-multi-tenant-configuration',
  imports: [
    CommonModule,
    AwDividerComponent,
    AwSearchComponent,
    AwTableComponent,
    AwSpinnerComponent,
    AwDialogComponent,
    AwToastComponent,
    AwButtonDirective,
    MatIcon,
    FormsModule,
  ],
  templateUrl: './multi-tenant-configuration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: [
    './multi-tenant-configuration.component.scss',
    '../../../../../styles/management.scss',
  ],
})
export class MultiTenantConfigurationComponent implements OnInit {
  private readonly MULTI_TENANT_ORG_ID = '00000000-0000-0000-0000-000000000000';

  constructor(
    private communicationPropService: CommunicationPropertiesService,
    private themeService: ThemeService,
    private userService: UserService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;
  @ViewChild(AwTableComponent, { static: true })
  awTableComponent!: AwTableComponent;

  isLoading: boolean = false;
  openDeleteOperationDialogSingle: boolean = false;
  openDeleteOperationDialogMultiple: boolean = false;

  container_theme = computed(() => this.themeService.themeSignal());

  userPermissions: Record<string, boolean> = {};

  singleOperationToDelete: TableRow | null = null;
  multipleOperationsToDelete: TableRow[] | null = null;

  operationsTableData = signal<any[]>([]);
  operationsTableDataCopy: any[] = [];
  disableOperationsDelete: boolean = true;
  operationSearchValue!: string;
  checkedRows: TableRow[] = [];
  addOperationMode: 'custom' | 'api' | 'signalr' = 'signalr';

  openAddInfoDialog: boolean = false;
  openEditInfoDialog: boolean = false;
  disableOperationsEdit: boolean = true;

  getFormFieldsData(): DialogFormControls {
    const controls: any[] = [
      {
        formControlName: 'operation_name',
        label: 'Operation Name',
        value: '',
        type: 'text',
        controlRequired: true,
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ];

    if (this.addOperationMode !== 'signalr') {
      controls.push({
        formControlName: 'callback_endpoint',
        label: 'Callback HTTP Endpoint',
        value: '',
        type: 'text',
        controlRequired: true,
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      });
    }

    return { controls };
  }

  getDialogOptions(): DialogOptions {
    const title = this.addOperationMode === 'signalr'
      ? 'Add SignalR Operation'
      : 'Add Operation';
    const description = this.addOperationMode === 'signalr'
      ? 'Enter the name for the new SignalR operation'
      : 'Enter all necessary information for your new operation here';
    return {
      variant: 'standard',
      title,
      description,
      secondaryButtonLabel: 'Cancel',
      primaryButtonLabel: 'Save',
    };
  }

  dialogOptionsDeleteSingle: DialogOptions = {
    variant: 'alert',
    title: 'Delete Operation',
    description:
      'By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  dialogOptionsDeleteMultiple: DialogOptions = {
    variant: 'alert',
    title: 'Delete Operation(s)',
    description:
      'By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  editFormFieldsData(): DialogFormControls {
    const isSignalR = this.checkedRows.length === 1
      && this.checkedRows[0]['endpoint_type'] === OperationTypes.SIGNALR;

    const controls: any[] = [
      {
        formControlName: 'operation_name',
        label: 'Operation Name',
        value: this.checkedRows.length === 1 ? this.checkedRows[0]['endpoint_name'] : '',
        type: 'text',
        controlRequired: true,
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ];

    if (!isSignalR) {
      controls.push({
        formControlName: 'callback_endpoint',
        label: 'Callback HTTP Endpoint',
        value: this.checkedRows.length === 1 ? this.checkedRows[0]['endpoint_callback'] : '',
        type: 'text',
        controlRequired: true,
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      });
    }

    return { controls };
  }

  editDialogOptions(): DialogOptions {
    return {
      variant: 'standard',
      title: 'Edit Operation',
      description: 'Update the operation information below',
      secondaryButtonLabel: 'Cancel',
      primaryButtonLabel: 'Save',
    };
  }

  operationsColumnsDefinition: TableCellInput[] = [
    {
      type: TableCellTypes.Title,
      key: 'endpoint_name',
      label: 'Endpoint Name',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'endpoint_type',
      label: 'Type',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'endpoint_id',
      label: 'Endpoint ID',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'endpoint_callback',
      label: 'Callback HTTP Endpoint',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
  ];

  buildAllData(): void {
    this.isLoading = true;

    this.communicationPropService
      .getCommunicationPropertiesById({
        organizationId: this.MULTI_TENANT_ORG_ID,
      })
      .pipe(
        catchError((error) => {
          this.buildToastMessage(
            'Failed to retrieve communication properties',
            'error'
          );
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((communicationProps) => {
        let operationData: any[] = [];
        if (communicationProps) {
          operationData =
            communicationProps.operations?.map((operation) => ({
              endpoint_name: operation.name ?? 'Not Found',
              endpoint_type: operation.operationType === OperationTypes.SIGNALR ? OperationTypes.SIGNALR : OperationTypes.HTTP,
              endpoint_id: operation.id ?? 'Not Found',
              endpoint_callback: operation.callbackHttpEndpoint ?? '',
            })) ?? [];
        }

        this.operationsTableData.set(operationData);
        this.operationsTableDataCopy = operationData;
      });
  }

  ngOnInit(): void {
    this.userPermissions = this.userService.getUserPermissions();

    if (this.userPermissions['canDelete'] || this.userPermissions['canEdit']) {
      this.operationsColumnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    if (this.userPermissions['canDelete']) {
      this.operationsColumnsDefinition.push({
        type: TableCellTypes.IconButton,
        key: 'random',
        label: 'Remove',
        buttonType: 'primary',
        isExpanded: false,
        icon: 'delete',
        action: (data: any) => {
          this.openDeleteOperationDialogSingle = true;
          this.singleOperationToDelete = data;
        },
      });
    }

    this.buildAllData();
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }) {
    this.checkedRows = checkedRows.rows;
    this.disableOperationsEdit = this.checkedRows.length !== 1;
    if (this.checkedRows.length > 0) {
      this.disableOperationsDelete = false;
      this.multipleOperationsToDelete = this.checkedRows;
    } else {
      this.disableOperationsDelete = true;
    }
  }

  onSearchTable(searchInput: string) {
    this.awTableComponent.applyFilter(searchInput);
  }

  dialogFormCloseHandler(key: string) {
    if (key === 'add') {
      this.openAddInfoDialog = false;
      this.addOperationMode = 'signalr';
    } else if (key === 'edit') {
      this.openEditInfoDialog = false;
    } else if (key === 'delete-single') {
      this.openDeleteOperationDialogSingle = false;
    } else if (key === 'delete-multiple') {
      this.openDeleteOperationDialogMultiple = false;
    }
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  addOperationFormSubmit(formValues: any) {
    this.isLoading = true;
    if (formValues.valid) {
      const formData: any = formValues.formData;
      const operationName = this.addOperationMode === 'api'
        ? `API-${formData.operation_name}`
        : formData.operation_name;
      const isSignalR = this.addOperationMode === 'signalr';
      const callbackEndpoint = isSignalR ? undefined : formData.callback_endpoint;
      const operationType = isSignalR ? OperationTypes.SIGNALR : OperationTypes.HTTP;

      const newCommunicationProp: CreateCommunicationPropertiesRequest = {
        operations: [
          { callbackHttpEndpoint: callbackEndpoint, name: operationName, operationType },
        ],
      };

      this.communicationPropService
        .createCommunicationProperties({
          organizationId: this.MULTI_TENANT_ORG_ID,
          body: newCommunicationProp,
        })
        .pipe(
          catchError((error) => {
            this.buildToastMessage('Failed to add operation', 'error');
            return of(null);
          }),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe((props) => {
          if (!props) {
            this.buildToastMessage('Failed to add operation', 'error');
            return;
          }

          const newOperation = props.operations?.[props.operations.length - 1];
          const newOperationId = newOperation?.id ?? '';

          this.openAddInfoDialog = false;
          this.addOperationMode = 'signalr';
          this.operationsTableData.set([
            ...this.operationsTableData(),
            {
              endpoint_name: operationName,
              endpoint_type: operationType,
              endpoint_id: newOperationId,
              endpoint_callback: callbackEndpoint ?? '',
            },
          ]);
          this.operationsTableDataCopy = [...this.operationsTableData()];
          this.buildToastMessage('Successfully added operation', 'success');
        });
    } else {
      this.buildToastMessage('Fill in all required fields', 'error');
      this.isLoading = false;
    }
  }

  editOperationFormSubmit(formValues: any): void {
    this.isLoading = true;
    if (formValues.valid) {
      const formData: any = formValues.formData;
      const operationName = formData.operation_name;
      const operationId = this.checkedRows[0]['endpoint_id'];
      const isSignalR = this.checkedRows[0]['endpoint_type'] === OperationTypes.SIGNALR;
      const callbackEndpoint = isSignalR ? undefined : formData.callback_endpoint;

      const updateRequest: UpdateCommunicationPropertiesRequest = {
        operation: { name: operationName, callbackHttpEndpoint: callbackEndpoint },
      };

      this.communicationPropService
        .updateCommunicationPropertiesOperation({
          organizationId: this.MULTI_TENANT_ORG_ID,
          operationId: operationId,
          body: updateRequest,
        })
        .pipe(
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe((success) => {
          if (success) {
            this.openEditInfoDialog = false;
            const updated = this.operationsTableDataCopy.map((row) =>
              row['endpoint_id'] === operationId
                ? { ...row, endpoint_name: operationName, endpoint_callback: callbackEndpoint }
                : row
            );
            this.operationsTableData.set(updated);
            this.operationsTableDataCopy = [...updated];
            this.buildToastMessage('Successfully updated operation', 'success');
          } else {
            this.buildToastMessage('Failed to update operation', 'error');
          }
        });
    } else {
      this.buildToastMessage('Fill in all required fields', 'error');
      this.isLoading = false;
    }
  }

  deleteOperationFormSubmit(): void {
    if (this.singleOperationToDelete === null) {
      this.buildToastMessage('No operation selected for deletion', 'error');
      return;
    }
    const operationId: string = this.singleOperationToDelete['endpoint_id'];

    this.isLoading = true;
    this.communicationPropService
      .deleteCommunicationPropertiesOperation({
        organizationId: this.MULTI_TENANT_ORG_ID,
        operationId: operationId,
      })
      .pipe(
        switchMap((operationDeleted) => {
          if (!operationDeleted) {
            this.buildToastMessage('Failed to delete operation', 'error');
            return of({
              commPropsDeleteAttempted: false,
              commPropsDeleted: false,
            });
          }

          this.buildToastMessage('Successfully deleted operation', 'success');
          const filtered = this.operationsTableDataCopy.filter(
            (op) => op['endpoint_id'] !== operationId
          );
          this.operationsTableData.set(filtered);
          this.operationsTableDataCopy = [...filtered];

          if (filtered.length === 0) {
            return this.communicationPropService
              .deleteCommunicationProperties({
                organizationId: this.MULTI_TENANT_ORG_ID,
              })
              .pipe(
                map((deletedAll) => ({
                  commPropsDeleteAttempted: true,
                  commPropsDeleted: deletedAll,
                }))
              );
          }

          return of({
            commPropsDeleteAttempted: false,
            commPropsDeleted: false,
          });
        }),
        finalize(() => {
          this.isLoading = false;
          this.openDeleteOperationDialogSingle = false;
        })
      )
      .subscribe(({ commPropsDeleteAttempted, commPropsDeleted }) => {
        if (commPropsDeleteAttempted && commPropsDeleted) {
          this.buildToastMessage(
            'Successfully deleted communication properties',
            'success'
          );
        }
      });
  }

  deleteSelectedOperations(): void {
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }

    const operationsToDelete: any[] = this.checkedRows.map(
      (row) => row['endpoint_id']
    );

    this.isLoading = true;
    from(operationsToDelete)
      .pipe(
        concatMap((operationId) =>
          this.communicationPropService
            .deleteCommunicationPropertiesOperation({
              organizationId: this.MULTI_TENANT_ORG_ID,
              operationId: operationId,
            })
            .pipe(
              map(() => ({ operationId, deleted: true })),
              catchError(() => of({ operationId, deleted: false }))
            )
        ),
        toArray(),
        finalize(() => {
          this.isLoading = false;
          this.openDeleteOperationDialogMultiple = false;
        })
      )
      .subscribe((results) => {
        const succeeded = results
          .filter((r) => r.deleted)
          .map((r) => r.operationId);
        const filtered = this.operationsTableDataCopy.filter(
          (row) => !succeeded.includes(row['endpoint_id'])
        );
        this.operationsTableData.set(filtered);
        this.operationsTableDataCopy = [...filtered];

        if (filtered.length === 0) {
          this.communicationPropService
            .deleteCommunicationProperties({
              organizationId: this.MULTI_TENANT_ORG_ID,
            })
            .subscribe((propsDeleted) => {
              this.buildToastMessage(
                propsDeleted
                  ? 'Successfully deleted communication properties'
                  : 'Failed to delete communication properties',
                propsDeleted ? 'success' : 'error'
              );
            });
        } else {
          const total = operationsToDelete.length;
          const num = succeeded.length;
          this.buildToastMessage(
            num === total
              ? 'Successfully deleted all selected operations'
              : `Deleted ${num} of ${total} operations`,
            num === total ? 'success' : 'alert'
          );
        }
      });
  }
}
